import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageTitle } from '@/components/PageTitle';
import { Field } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toaster';
import { errorText } from '@/lib/errors';
import { formatDateTime } from '@/lib/format';
import { requirePermission } from '@/lib/guards';
import { client, orpc } from '@/lib/orpc';
import { Link } from '@/lib/router-compat';
import { useCan } from '@/lib/session';

export const Route = createFileRoute('/_app/dashboards/')({
  beforeLoad: ({ context }) => requirePermission(context.me, 'dashboards.view'),
  component: DashboardsPage
});

function DashboardsPage() {
  const { t } = useTranslation();
  const can = useCan();
  const list = useQuery(orpc.dashboards.list.queryOptions());
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-6 p-6">
      <PageTitle
        title={t('dashboards.title')}
        items={[{ label: t('dashboards.title'), active: true }]}
        actions={
          can('dashboards.edit') && (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
              <span className="iconify lucide--plus size-4" />
              {t('dashboards.new')}
            </button>
          )
        }
      />
      {list.data?.length === 0 && (
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <p className="text-base-content/60">{t('dashboards.none')}</p>
          </div>
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {list.data?.map((d) => (
          <Link key={d.id} to={`/dashboards/${d.id}`} className="card bg-base-100 hover:bg-base-200/40 shadow-sm transition-colors">
            <div className="card-body gap-2">
              <div className="flex items-start gap-2.5">
                <div className="bg-base-200 mask mask-squircle flex size-9 shrink-0 items-center justify-center">
                  <span className="iconify lucide--layout-dashboard size-4.5" />
                </div>
                <div className="min-w-0">
                  <h2 className="truncate font-medium">{d.title}</h2>
                  <p className="text-base-content/60 text-xs">
                    {d.owner?.name ?? '–'} · {formatDateTime(d.updatedAt)}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {d.publishedVersion === null && <span className="badge badge-ghost badge-sm">{t('dashboards.notPublished')}</span>}
                {d.visibility === 'restricted' && (
                  <span className="badge badge-ghost badge-sm gap-1">
                    <span className="iconify lucide--lock size-3" />
                    {t('dashboards.visibilityRestricted')}
                  </span>
                )}
                {d.pendingProposals > 0 && <span className="badge badge-info badge-soft badge-sm">{t('dashboards.proposals', { count: d.pendingProposals })}</span>}
                {d.publishedVersion !== null && d.latestVersion > d.publishedVersion && d.access !== 'viewer' && (
                  <span className="badge badge-warning badge-soft badge-sm">{t('dashboards.statuses.draft')}</span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
      <CreateDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function CreateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [template, setTemplate] = useState<'' | 'linkedin_performance' | 'lead_funnel'>('linkedin_performance');
  const [visibility, setVisibility] = useState<'all' | 'restricted'>('all');
  const create = useMutation({
    mutationFn: () => client.dashboards.create({ title: title.trim(), visibility, ...(template && { template }) }),
    onSuccess: ({ id }) => void navigate({ to: '/dashboards/$dashboardId', params: { dashboardId: id } }),
    onError: (e) => toast('error', errorText(t, e))
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('dashboards.new')}
      actions={
        <>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>
            {t('admin.cancel')}
          </button>
          <button type="button" className="btn btn-sm btn-primary" disabled={!title.trim() || create.isPending} onClick={() => create.mutate()}>
            {t('dashboards.create')}
          </button>
        </>
      }
    >
      <Field label={t('dashboards.name')}>{(id) => <input id={id} className="input w-full" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />}</Field>
      <Field label={t('dashboards.template')}>
        {(id) => (
          <select id={id} className="select w-full" value={template} onChange={(e) => setTemplate(e.target.value as typeof template)}>
            <option value="">{t('dashboards.templates.empty')}</option>
            <option value="linkedin_performance">{t('dashboards.templates.linkedin_performance')}</option>
            <option value="lead_funnel">{t('dashboards.templates.lead_funnel')}</option>
          </select>
        )}
      </Field>
      <Field label={t('dashboards.visibility')}>
        {(id) => (
          <select id={id} className="select w-full" value={visibility} onChange={(e) => setVisibility(e.target.value as typeof visibility)}>
            <option value="all">{t('dashboards.visibilityAll')}</option>
            <option value="restricted">{t('dashboards.visibilityRestricted')}</option>
          </select>
        )}
      </Field>
    </Modal>
  );
}
